import React, { useState, useRef, useEffect, useMemo } from 'react'
import './HomePage2.css'
import { useCatalog } from './CatalogContext'
import ProductCard from './ProductCard';
import Testimonials from './Testimonials';
import QuoteComponent from './QuoteComponent';
import CustomizedCake from './CustomizedCake';
import Footer from './Footer';
import DynamicScrollIslandTOC from './DynamicScrollIslandTOC';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { useNavigate } from 'react-router-dom';
import { fetchBlogs, STATIC_BLOGS } from '../data/blogs';

// Press / partners. Logo files live in /public - keep the name as the alt text
// and as the fallback if an image ever fails to load.
// `large: true` for wordmarks that read small at the shared logo height.
const FEATURED_IN = [
  { name: 'Fife Today', logo: '/fifetoday.PNG', large: true },
  { name: 'The Scotsman', logo: '/thescotsman.PNG' },
  { name: 'Scottish Asian Business Chamber', logo: '/ScottishAsianBusinessChamber.png' },
  { name: 'University of Glasgow', logo: '/UniversityVIAVERITASVITAofGlasgow.PNG', large: true },
  { name: 'Awaz FM', logo: '/awazfm.png' },
  { name: 'London Tech Week', logo: '/LONDONTECHWEEK.PNG' },
  { name: 'Paralympics', logo: '/paralympics.PNG' },
  { name: 'UN Women', logo: '/unwomen.png' },
];


function HomePage2() {
  const scrollRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const navigate = useNavigate();
  const { categories: catalogCategories, topProducts } = useCatalog();
  const categories = useMemo(() => catalogCategories.map(c => ({
    name: c.Name,
    value: c._id,
    originalName: c.Name
  })), [catalogCategories]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const loading = false;
  const error = null;
  const [activeFilter, setActiveFilter] = useState(null);
  const [blogPosts, setBlogPosts] = useState(STATIC_BLOGS);

  // Load the latest journal posts (top 3) from the API.
  useEffect(() => {
    let active = true;
    (async () => {
      const blogs = await fetchBlogs();
      if (active && blogs && blogs.length) setBlogPosts(blogs.slice(0, 3));
    })();
    return () => { active = false; };
  }, []);

  // Below 600px the "As featured in" logos scroll as a marquee instead of
  // sitting in a static row.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const featuredTiles = FEATURED_IN.map((item) => (
    <div key={item.name} className="featuredInItem">
      {item.logo && (
        <img
          src={item.logo}
          alt={item.name}
          className={`featuredInLogo ${item.large ? 'featuredInLogo--lg' : ''}`}
          loading="lazy"
          onError={(e) => {
            // Fall back to the name if the file is missing/renamed
            e.target.style.display = 'none';
            e.target.parentElement?.classList.add('featuredInItem--noLogo');
          }}
        />
      )}
      <span className="featuredInName">{item.name}</span>
    </div>
  ));

  // Keep the active filter aligned with the loaded categories
  useEffect(() => {
    if (categories.length === 0) return;
    setActiveFilter(prev => {
      if (prev && categories.some(c => c.value === prev.value)) return prev;
      return categories[0];
    });
  }, [categories]);


  // Update display products when activeFilter changes
  useEffect(() => {
    if (!activeFilter || topProducts.length === 0) return;


    try {
      // Find top products for the selected category
      const categoryTopProducts = topProducts.find(
        topProduct => topProduct.category._id === activeFilter.value
      );


      if (categoryTopProducts) {
        // Transform the 3 products (Product1, Product2, Product3) to match frontend structure
        const transformedProducts = [];
        
        // Helper function to transform a single product
        const transformProduct = (product) => {
          if (product) {
            return {
              id: product._id,
              name: product.productName,
              tagline: product.Tagline,
              description: product.ProductDesc,
              price: product.Price,
              weight: product.Weight,
              image: (product.Images && product.Images[0]) || '',
              ingredients: product.Ingredients,
              issoldout: product.isSoldOut
            };
          }
          return null;
        };
        
        // Transform all 3 products
        const product1 = transformProduct(categoryTopProducts.Product1);
        const product2 = transformProduct(categoryTopProducts.Product2);
        const product3 = transformProduct(categoryTopProducts.Product3);


        [product1, product2, product3].forEach(product => {
          if (product) {
            transformedProducts.push(product);
          }
        });
        
        setDisplayProducts(transformedProducts);
      } else {
        // No top products found for this category
        setDisplayProducts([]);
      }
    } catch (err) {
      console.error('Error processing top products:', err);
      setDisplayProducts([]);
    }
  }, [activeFilter, topProducts]);



  const animation = {
    variants: {
      hidden: { 
        opacity: 0, 
        filter: "blur(10px)",
        y: 20
      },
      show: { 
        opacity: 1, 
        filter: "blur(0px)",
        y: 0
      },
    },
    initial: "hidden",
    animate: "show",
    exit: "hidden",
    transition: { duration: 0.6, ease: "easeInOut" }
  };



  const noProductsAnimation = {
    variants: {
      hidden: { 
        opacity: 0, 
        y: 20
      },
      show: { 
        opacity: 1, 
        y: 0
      },
    },
    initial: "hidden",
    animate: "show",
    exit: "hidden",
    transition: { duration: 0.5, ease: "easeInOut" }
  };



  if (loading) {
    return (
      <div className="loading-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }



  if (error) {
    return (
      <div className="error-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        fontSize: '1.2rem',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }



  return (
    <>
      <div className='homePageMainContainer'>
        <div className="main-content-wrapper">
          <div className="foundersFavsBanner">
            <h2 className="foundersFavsTitle">Founder's Favs</h2>
            <div className="filter-wrapper">
              <DynamicScrollIslandTOC
                data={categories}
                value={activeFilter}
                setValue={setActiveFilter}
                onOpenChange={setIsDropdownOpen}
                ref={scrollRef}
              />
            </div>
          </div>
          <div className={`content-blur-wrapper ${isDropdownOpen ? 'blurred' : ''}`}>
            <motion.div 
              className="products-section"
              style={{ marginTop: '20px' }}
            >
              <MotionConfig transition={{ duration: 0.6 }}>
                <div className='productsGrid cookiesGrid' ref={scrollRef}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeFilter?.value || 'default'}
                      className="products-container"
                      {...animation}
                    >
                      {displayProducts.length > 0 ? (
                        displayProducts.map(product => (
                          <div className="product-card-grid-wrapper" key={product.id}>
                            <ProductCard cookie={product} />
                          </div>
                        ))
                      ) : (
                        <motion.div
                          className="no-products-message"
                          {...noProductsAnimation}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            minHeight: '220px',
                            fontSize: '1.2rem',
                            color: '#666',
                            fontWeight: '500',
                            textAlign: 'center'
                          }}
                        >
                          No products in this category yet
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </MotionConfig>
            </motion.div>
          </div>
          <div className='viewAllContainer'>
            <p onClick={()=>{navigate('/allproduct')}}>VIEW ALL</p>
          </div>
        </div>
        {/* Featured In / Who Trusts Us */}
        <div className="featuredInSection">
          <p className="featuredInLabel">AS FEATURED IN & TRUSTED BY</p>
          {/* Phones get a continuously scrolling marquee (same as the
              testimonials); laptops show the whole row at once. */}
          {isNarrow ? (
            <Marquee speed={40} gradient={false} pauseOnHover pauseOnClick className="featuredInMarquee">
              <div className="featuredInLogos featuredInLogos--marquee">{featuredTiles}</div>
            </Marquee>
          ) : (
            <div className="featuredInLogos">{featuredTiles}</div>
          )}
          <div className="trustedByRow">
            <div className="trustedStat"><span className="trustedNum">2000+</span><span className="trustedLabel">Happy Customers</span></div>
            <div className="trustedDivider" />
            <div className="trustedStat"><span className="trustedNum">25+</span><span className="trustedLabel">Signature Products</span></div>
            <div className="trustedDivider" />
            <div className="trustedStat"><span className="trustedNum">50+</span><span className="trustedLabel">Events Hosted</span></div>
            <div className="trustedDivider" />
            <div className="trustedStat"><span className="trustedNum">4.9★</span><span className="trustedLabel">Average Rating</span></div>
          </div>
        </div>

        {/* Blog / SEO Section */}
        <div className="blogSection">
          <div className="blogHeader">
            <p className="blogSectionLabel">FROM THE JOURNAL</p>
            <h2 className="blogSectionTitle">Stories, Wellness & the World of Elaichi</h2>
          </div>
          <div className="blogGrid">
            {blogPosts.map(post => (
              <div
                key={post._id || post.id}
                className="blogCard"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/blog/${post.slug}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/blog/${post.slug}`); }}
              >
                {post.category && <div className="blogCardCategory">{post.category}</div>}
                <h3 className="blogCardTitle">{post.title}</h3>
                <p className="blogCardExcerpt">{post.excerpt}</p>
                {post.readTime && <span className="blogCardReadTime">{post.readTime} →</span>}
              </div>
            ))}
          </div>
          <div className="blogSeeAllWrap">
            <button className="blogSeeAllBtn" onClick={() => navigate('/blog')}>
              See our blogs →
            </button>
          </div>
        </div>

        <div className={`content-blur-wrapper ${isDropdownOpen ? 'blurred' : ''}`}>
          <QuoteComponent/>
          <CustomizedCake/>
          <Testimonials/>
        </div>
      </div>
      <Footer/>
    </>
  )
}


export default HomePage2
